import { api } from './api';
import {
  PurchaseOrder,
  CreatePurchaseOrderData,
  UpdatePurchaseOrderData,
  PurchaseOrderFilters,
  PurchaseOrdersResponse
} from '../types/purchaseOrder';

export const purchaseOrderService = {
  async getPurchaseOrders(filters: PurchaseOrderFilters = {}): Promise<PurchaseOrdersResponse> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.vendorId) params.append('vendorId', filters.vendorId);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));

    const response = await api.get(`/purchase-orders?${params.toString()}`);
    return response.data;
  },

  async getPurchaseOrder(id: string): Promise<PurchaseOrder> {
    const response = await api.get(`/purchase-orders/${id}`);
    return response.data;
  },

  async createPurchaseOrder(data: CreatePurchaseOrderData): Promise<PurchaseOrder> {
    const response = await api.post('/purchase-orders', data);
    return response.data;
  },

  async updatePurchaseOrder(id: string, data: UpdatePurchaseOrderData): Promise<PurchaseOrder> {
    const response = await api.put(`/purchase-orders/${id}`, data);
    return response.data;
  },

  async deletePurchaseOrder(id: string): Promise<void> {
    const response = await api.delete(`/purchase-orders/${id}`);
    return response.data;
  },

  async markPurchaseOrderAsOrdered(id: string): Promise<PurchaseOrder> {
    const response = await api.patch(`/purchase-orders/${id}/order`);
    return response.data;
  },

  async cancelPurchaseOrder(id: string): Promise<PurchaseOrder> {
    const response = await api.patch(`/purchase-orders/${id}/cancel`);
    return response.data;
  },

  // Helper function to calculate line totals
  calculateLineTotal(
    quantity: number,
    rate: number,
    taxRate: number = 0
  ): { amount: number; taxAmount: number; total: number } {
    const amount = quantity * rate;
    const taxAmount = (amount * taxRate) / 100;
    const total = amount + taxAmount;

    return { amount, taxAmount, total };
  },

  // Helper function to calculate PO totals
  calculatePOTotals(
    items: { quantity: number; rate: number; taxRate?: number }[],
    shippingCost: number = 0
  ): { subtotal: number; taxAmount: number; total: number } {
    let subtotal = 0;
    let taxAmount = 0;

    items.forEach((item) => {
      const lineTotal = this.calculateLineTotal(
        item.quantity,
        item.rate,
        item.taxRate || 0
      );
      subtotal += lineTotal.amount;
      taxAmount += lineTotal.taxAmount;
    });

    const total = subtotal + taxAmount + shippingCost;

    return { subtotal, taxAmount, total };
  }
};
